import { makeStyles, Menu, Theme, MenuItem } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import {
  ReactNode,
  useContext,
  useState,
  Children,
  useRef,
  cloneElement,
  isValidElement,
  ReactElement,
} from 'react';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import { WbButton } from '../WbButton';
import { WbSidebarConfigContext } from '../WbSidebar/config';
import { WbBreadcrumb, WbBreadcrumbProps } from './WbBreadcrumb';

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    height: '32px',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.bkg.secondary,
    position: 'relative',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    height: '100%',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderRadius: '0px',
  },
  menuPaper: {
    width: '100vw',
    maxWidth: '100vw',
    background: theme.palette.white,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(0),
    borderRadius: '0px',
    left: '0px !important',
  },
  menuList: {
    padding: theme.spacing(0),
  },
  menuItem: {
    minHeight: '32px',
    padding: '0px',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    color: theme.palette.primary.main,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  // Pass this class 2 times (with &&) to increase the specificity and override the default styles of the Link component
  link: {
    '&&': {
      padding: theme.spacing(0.5, 1),
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflowWrap: 'anywhere',
      display: 'block',
      width: '100%',
    },
  },
}));

export const WbMobileBreadcrumbMenu = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { sidebarConfig } = useContext(WbSidebarConfigContext);
  const classes = useStyle(sidebarConfig);
  const theme = useTheme();
  const anchorEl = useRef(null);
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <div className={classes.root} ref={anchorEl}>
        <WbButton
          onClick={handleOpen}
          variant="text"
          color="default"
          className={classes.button}
          icon={<MenuOpenIcon fontSize="small" />}
          label="Back"
        />
      </div>
      <Menu
        keepMounted
        open={open}
        anchorEl={anchorEl.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        classes={{
          paper: classes.menuPaper,
          list: classes.menuList,
        }}
      >
        {Children.toArray(children).map((child, index) => {
          // Validate that child is a valid React element and a WbBreadcrumb component
          if (!isValidElement(child) || child.type !== WbBreadcrumb) {
            return null;
          }

          // Cast to WbBreadcrumb element for type-safe prop access
          const breadcrumbElement = child as ReactElement<WbBreadcrumbProps>;

          return (
            <MenuItem className={classes.menuItem} button={false} key={index}>
              {cloneElement(breadcrumbElement, {
                linkClassName: classes.link,
                style: {
                  paddingLeft: theme.spacing(1 + 1 * index),
                },
              })}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
